import * as React from 'react';
import { cn } from '@/lib/utils';
import { Upload, X, File } from 'lucide-react';

interface FileInputProps
  extends Omit<React.ComponentProps<'input'>, 'type' | 'value' | 'onChange'> {
  multiple?: boolean;
  accept?: string;
  value?: File[];
  onChange?: (files: File[]) => void;
  maxSize?: number; // in bytes
  maxFiles?: number;
  disabled?: boolean;
  placeholder?: string;
}

const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      className,
      multiple = false,
      accept,
      value = [],
      onChange,
      maxSize,
      maxFiles,
      disabled = false,
      placeholder = multiple
        ? 'Kéo thả file vào đây hoặc click để chọn'
        : 'Kéo thả file vào đây hoặc click để chọn',
      ...props
    },
    ref
  ) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const [error, setError] = React.useState<string>('');
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(ref, () => inputRef.current!);

    const validateFile = (file: File): string | null => {
      if (maxSize && file.size > maxSize) {
        return `File "${file.name}" quá lớn. Tối đa ${(
          maxSize /
          1024 /
          1024
        ).toFixed(1)}MB`;
      }

      if (accept) {
        const acceptedTypes = accept
          .split(',')
          .map((type) => type.trim().toLowerCase());
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        const mimeType = file.type.toLowerCase();

        const isValidType = acceptedTypes.some((acceptedType) => {
          if (acceptedType.startsWith('.')) {
            return fileExtension === acceptedType;
          }
          return mimeType.match(acceptedType.replace('*', '.*'));
        });

        if (!isValidType) {
          return `File "${file.name}" không đúng định dạng.`;
        }
      }

      return null;
    };

    const processFiles = (fileList: FileList) => {
      const files = Array.from(fileList);
      const validFiles: File[] = [];
      let errorMessage = '';

      for (const file of files) {
        const validation = validateFile(file);
        if (validation) {
          errorMessage = validation;
          break;
        }
        validFiles.push(file);
      }

      if (errorMessage) {
        setError(errorMessage);
        return;
      }

      if (maxFiles && validFiles.length > maxFiles) {
        setError(`Chỉ được chọn tối đa ${maxFiles} file(s)`);
        return;
      }

      setError('');

      if (multiple) {
        const newFiles = [...value, ...validFiles];
        if (maxFiles && newFiles.length > maxFiles) {
          setError(`Chỉ được chọn tối đa ${maxFiles} file(s)`);
          return;
        }
        onChange?.(newFiles);
      } else {
        onChange?.(validFiles.slice(0, 1));
      }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    };

    const removeFile = (index: number) => {
      const newFiles = value.filter((_, i) => i !== index);
      onChange?.(newFiles);
      setError('');
    };

    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
      <div className="w-full">
        <div
          className={cn(
            'relative border-2 border-dashed rounded-lg transition-colors duration-200 ease-in-out',
            isDragging && !disabled
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50',
            disabled && 'opacity-50 cursor-not-allowed',
            error && 'border-destructive',
            className
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            type="file"
            multiple={multiple}
            accept={accept}
            onChange={handleFileChange}
            disabled={disabled}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
            {...props}
          />

          <div className="flex flex-col items-center justify-center p-6 text-center">
            <Upload
              className={cn(
                'w-12 h-12 mb-3 transition-colors',
                isDragging ? 'text-primary' : 'text-muted-foreground'
              )}
            />
            <p className="text-muted-foreground mb-1 text-xl font-bold">
              {placeholder}
            </p>
            {accept && (
              <p className="text-sm text-muted-foreground">
                Hỗ trợ các định dạng: {accept}
              </p>
            )}
            {maxSize && (
              <p className="text-sm text-muted-foreground">
                Kích thước mỗi tệp tối đa: {(maxSize / 1024 / 1024).toFixed(0)}
                MB
              </p>
            )}
            {maxFiles && (
              <p className="text-sm text-muted-foreground">
                Số lượng tệp tối đa: {maxFiles}
              </p>
            )}
          </div>
        </div>

        {error && <p className="text-sm text-destructive mt-1">{error}</p>}

        {value.length > 0 && (
          <div className="mt-3 space-y-2">
            {value.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between p-2 bg-muted/50 rounded-md border"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="cursor-pointer flex-shrink-0 p-1 hover:bg-destructive/10 rounded transition-colors"
                  >
                    <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
);

FileInput.displayName = 'FileInput';

export { FileInput, type FileInputProps };
